<script setup lang="ts">
import './assets/style.scss'
import { computed, nextTick, type PropType, ref } from 'vue'
import { VIcons, VInventAccentStatus } from '@/shared/ui'
import { WObjectCoordsManuallyChangeForm } from '@/widgets'
import { InventStats } from '@/entities/Inventory/Stats'
import { ObjectFileGeoGsonBtn } from '@/features/Inventory/ObjectFileGeoGson'
import { InventObjectFileLoader } from '@/features/Inventory/ObjectFileLoader'
import { useRoute, useRouter } from 'vue-router'
import { VDefaultsProvider } from 'vuetify/components'

const props = defineProps({
  fieldsMeta: {
    type: Object as PropType<any>,
    required: true,
  },
  fieldsLocked: {
    type: Boolean,
    default: false,
  },
  /** Create V2 batch: hide passport-only toolbar (status, history, geojson, stats). */
  draftMode: {
    type: Boolean,
    default: false,
  },
  /** Draft coordinates for manual edit when `draftMode` is true. */
  draftCoordinates: {
    type: Array as PropType<number[][]>,
    default: () => [],
  },
})

const open = defineModel<boolean>('open', {
  default: false,
})
const fields = defineModel<{ [id: string]: string }>('fields', {
  required: true,
})
const element = defineModel<any>('element', {
  required: true,
})

const emit = defineEmits<{
  'update:draftCoordinates': [value: number[][]]
}>()

const router = useRouter()
const route = useRoute()

const typeFields = computed<any[]>(() => {
  const raw = (element.value?.invent_object_type?.fields ?? []) as any[]
  return raw.filter((f: any) => !f.is_table && f.type === 'link_object')
})

const isSinglePoint = computed<boolean>(() => element.value?.coordinates?.length === 1)

const mergedFields = computed<any[]>(() => {
  const existing = new Map<string, any>()
  for (const f of props.fieldsMeta ?? []) {
    existing.set(String(f.id), f)
  }
  for (const f of typeFields.value) {
    if (!existing.has(String(f.id))) {
      existing.set(String(f.id), f)
    }
  }
  return [...existing.values()]
})

/** Поисковый запрос для фильтрации полей по названию */
const search = ref('')

/**
 * Отображаемые поля: отсеиваем системные/табличные-связи и фильтруем
 * по введённому запросу (совпадение по названию, без учёта регистра).
 */
const visibleFields = computed<any[]>(() => {
  const base = mergedFields.value.filter(
    (field) => !field.is_system && !(field.is_table && field.link_target_type_id),
  )
  const query = search.value.trim().toLowerCase()

  if (!query) return base

  return base.filter((field) =>
    String(field.name ?? '')
      .toLowerCase()
      .includes(query),
  )
})

function onDrawerFieldUpdate(fieldId: string | number, v: unknown) {
  fields.value = {
    ...fields.value,
    [String(fieldId)]: v == null ? '' : String(v),
  }
}

/**
 * Функция обработки закрытия окна
 */
const closeHandler = () => {
  open.value = false
}

/**
 * Прокручивает содержимое списка в самый низ (к форме изменения координат).
 * Открывает списка при необходимости и ждёт отрисовки контента.
 */
const bottomAnchor = ref<HTMLElement | null>(null)

/** Above `.pass-run-v2-field-list__drawer` (999999) so nested Vuetify overlays stay visible. */
const FIELD_LIST_OVERLAY_Z = 1_000_010

const fieldListOverlayDefaults = {
  VTooltip: {
    zIndex: FIELD_LIST_OVERLAY_Z,
  },
  VSpeedDial: {
    zIndex: FIELD_LIST_OVERLAY_Z,
  },
  VMenu: {
    zIndex: FIELD_LIST_OVERLAY_Z,
  },
}

const scrollToBottom = async () => {
  open.value = true
  await nextTick()
  bottomAnchor.value?.scrollIntoView({ behavior: 'auto', block: 'end' })
}

defineExpose({ scrollToBottom })

/** Ссылка на историю изменений */
const historyLink = computed(() => {
  return router.resolve({
    name: 'object.inventory.history',
    params: {
      ...route.params,
      tab: 'inventory',
      tab_inventory: 'history',
    },
  })
})
</script>

<template>
  <Teleport to="body">
    <VNavigationDrawer
      class="pass-run-v2-field-list__drawer"
      v-model="open"
      temporary
      location="right"
      :width="400"
    >
      <VDefaultsProvider :defaults="fieldListOverlayDefaults">
        <div class="pass-run-v2-field-list__header">
          <div>
            <span class="pass-run-v2-field-list__title">
              {{ $t('itf.passport_runtime_detailed_view_title') }}
            </span>

            <span class="pass-run-v2-field-list__subtitle">
              {{ $t('itf.passport_runtime_detailed_view_hint') }}
            </span>
          </div>

          <VBtn icon density="compact" variant="text" @click="closeHandler">
            <VIcons type="close" />
          </VBtn>
        </div>

        <VDivider class="my-4" />

        <template v-if="!draftMode">
          <div class="pass-run-v2-field-list__bts">
            <VInventAccentStatus v-if="element.accept_status" :status="element.accept_status" />

            <VBtn :to="historyLink" density="compact">Ист. изм.</VBtn>

            <ObjectFileGeoGsonBtn :item="element" />
          </div>
        </template>

        <VCheckbox
          :label="$t('itf.is_project')"
          class="mb-3"
          density="compact"
          hide-details
          v-model="element.is_projecting"
          :readonly="fieldsLocked"
        />

        <VTextarea
          :label="$t('itf.comment')"
          v-model="element.comment"
          hide-details
          :readonly="fieldsLocked"
        />

        <VDivider class="my-4" />

        <InventObjectFileLoader v-model="element" :draft-mode="draftMode" />

        <InventStats
          v-if="!draftMode && element.stats && Object.keys(element.stats.by_type).length"
          hide-btn
          :stats="element.stats"
        />

        <VDivider class="my-4" />

        <WObjectCoordsManuallyChangeForm
          v-if="draftMode"
          draft-mode
          :draft-coordinates="draftCoordinates"
          :fields-locked="fieldsLocked"
          :default-open="draftCoordinates.length === 1"
          @update:draft-coordinates="emit('update:draftCoordinates', $event)"
        />

        <WObjectCoordsManuallyChangeForm
          v-else-if="element.id"
          :object-id="element.id"
          :default-open="isSinglePoint"
        />

        <div ref="bottomAnchor" />
      </VDefaultsProvider>
    </VNavigationDrawer>
  </Teleport>
</template>
